import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import { apiEndpoint } from '/shared/util/Statics';
import { globalUserRoles } from '/business/shared/util/Statics';

import getUserSelector from '/business/shared/state/selectors/auth/getUserSelector';

import LinkButton from '/shared/components/widgets/button/LinkButton';

import StyledContainer from '/shared/styled/StyledContainer';
import StyledInner from '/shared/styled/StyledInner';
import StyledFooter from '/shared/styled/layout/footer/StyledFooter';
import StyledFooterButton from '/shared/styled/layout/footer/StyledFooterButton';

import useAppContext from '/business/shared/hooks/useAppContext';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import type { AuthAccount } from '/business/shared/state/reducers/auth';

function hasCommonValues(arr1: AuthAccount['globalRole'] | undefined, arr2: readonly string[]): boolean {
  if (arr1 && arr2) {
    return arr1.some((value) => arr2.includes(value));
  }
  return false;
}

function Footer() {
  const user = useSelector(getUserSelector);
  const appId = useSelector(getApplicationIdSelector);
  const { getApplicationUrl } = useUrlBuilder();

  const { settings } = useAppContext();
  const showUserGuideLinks = settings?.allowUserGuideDownloads;
  const allowForOwner = settings?.allowOwnerDownloadUserGuide;

  const privacyPolicyUrl = '/privacy-policy';
  const cookieUrl = '/cookie-preferences';
  const customerUserGuideUrl = `${apiEndpoint}/api/b2b/get_static_file?type=userguide-customer`;
  const supportUserGuideUrl = `${apiEndpoint}/api/b2b/get_static_file?type=userguide-support`;
  const methodologyUrl = `${apiEndpoint}/api/b2b/get_static_file?type=methodology`;
  const termsConditionUrl = `${apiEndpoint}/api/b2b/get_static_file?type=termsconditions`;

  const showSupportUserGuide = hasCommonValues(user?.globalRole, [
    globalUserRoles.ADMIN,
    globalUserRoles.APPLICATION_SUPPORT,
  ]);
  const showOwnerUserGuide = showSupportUserGuide ? true : allowForOwner;

  return (
    <StyledFooter $business>
      <StyledContainer>
        <StyledInner $footer>
          <div>
            {appId !== null && appId > 0 && (
              <StyledFooterButton>
                <LinkButton $small to={getApplicationUrl('/third-party-assurance', appId)}>
                  Third-party assurance
                </LinkButton>
              </StyledFooterButton>
            )}
            {!!user && methodologyUrl && (
              <div>
                <LinkButton $small href={methodologyUrl}>
                  {/* @i18n: Methodology Statement */}
                  <Trans i18nKey="business:MethodologyPage.navLink" />
                </LinkButton>
              </div>
            )}
            <div>
              <LinkButton $small to={privacyPolicyUrl}>
                {/* @i18n: Privacy Statement */}
                <Trans i18nKey="business:PrivacyPolicyPage.navLink" />
              </LinkButton>
            </div>
            {termsConditionUrl && (
              <div>
                <LinkButton $small href={termsConditionUrl}>
                  {/* @i18n: Terms & Conditions */}
                  <Trans i18nKey="business:TermsConditionsPage.navLink" />
                </LinkButton>
              </div>
            )}
            <div>
              <LinkButton $small to={cookieUrl}>
                {/* @i18n: Cookie preferences */}
                <Trans i18nKey="business:CookiePage.navLink" />
              </LinkButton>
            </div>
            {!!user && showUserGuideLinks && showOwnerUserGuide && (
              <div>
                <LinkButton $small href={customerUserGuideUrl}>
                  {/* @i18n: Customer User Guide */}
                  <Trans i18nKey="business:UserGuide.navLink" />
                </LinkButton>
              </div>
            )}
            {showSupportUserGuide && showUserGuideLinks && (
              <div>
                <LinkButton $small href={supportUserGuideUrl}>
                  {/* @i18n: Support User Guide */}
                  <Trans i18nKey="business:SupportUserGuide.navLink" />
                </LinkButton>
              </div>
            )}
            <div>
              <small>
                {/* @i18n: All rights reserved © */}
                <Trans i18nKey="business:Snippets.allRightsReserved" />
              </small>
            </div>
          </div>
        </StyledInner>
      </StyledContainer>
    </StyledFooter>
  );
}

export default Footer;
